import { DateTime } from 'luxon'
import { createSelector } from 'reselect'
import { times } from 'lodash'
import currency from 'currency.js'

import {
  filterTransactionsByType,
  getUsersTransactions,
} from '@/featuresV1//Transactions/transactions.selectors'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { addCurrencyArray, centsToCurrency } from '@/utils/currencyHelpers'
import { isoToUTCDateTime } from '@/utils/dateHelpers'

const addTransactions = (transactions: Transaction[]) =>
  addCurrencyArray(transactions.map((t) => t.amountInCents))

const getTransactionsWithinRange = (
  transactions: Transaction[],
  start: DateTime,
  end: DateTime
) =>
  transactions.filter((transaction) => {
    const transactionDate = DateTime.fromISO(transaction.date)

    return transactionDate >= start && transactionDate <= end
  })

const getIncomeForMonth = (
  transactions: { [p: string]: Transaction },
  month: DateTime
) => {
  const filteredTransactions = getTransactionsWithinRange(
    filterTransactionsByType(transactions, 'income'),
    month.startOf('month'),
    month.endOf('month')
  )

  return addTransactions(filteredTransactions)
}

export const selectIncomeForMonth = createSelector(
  getUsersTransactions,
  (_: unknown, month: DateTime) => month,
  getIncomeForMonth
)

export const selectPercDiffLastMonth = createSelector(
  getUsersTransactions,
  (_: unknown, currentMonth: DateTime) => currentMonth,
  (_: unknown, __: unknown, pastMonth: DateTime) => pastMonth,
  (userTransactions, currentMonth, pastMonth) => {
    const currentIncome = getIncomeForMonth(userTransactions, currentMonth)
    const aMonthAgo = Math.min(pastMonth.endOf('month').day, currentMonth.day)

    const filteredTransactions = getTransactionsWithinRange(
      filterTransactionsByType(userTransactions, 'income'),
      pastMonth.startOf('month'),
      pastMonth.set({ day: aMonthAgo }).endOf('day')
    )

    const pastIncome = addTransactions(filteredTransactions)

    // Avoid calculations that return infinity or 100%
    if (!currentIncome || !pastIncome) {
      return null
    }

    return (currentIncome - pastIncome) / pastIncome
  }
)

export const selectMonthToDateIncomeChartData = createSelector(
  getUsersTransactions,
  (_: unknown, pastValue: 'month' | 'year') => pastValue,
  (userTransactions, pastValue) => {
    const today = DateTime.now()

    const pastDate =
      pastValue === 'month'
        ? today.minus({ months: 1 })
        : today.minus({ years: 1 })

    const currentMonthStart = today.startOf('month')
    const currentMonthEnd = today.endOf('month')

    const pastMonthStart = pastDate.startOf('month')
    const pastMonthEnd = pastDate.endOf('month')

    const maxDays = Math.max(
      today.endOf('month').day,
      pastDate.endOf('month').day
    )
    const incomeTransactions = filterTransactionsByType(
      userTransactions,
      'income'
    )
    const totalsPerDay = new Map<number, currency>()
    const pastTotalsPerDay = new Map<number, currency>()

    // Calculate totals for each day, throw into maps for lookup
    incomeTransactions.forEach((transaction) => {
      const transactionDate = isoToUTCDateTime(transaction.date)
      const day = transactionDate.day

      if (
        transactionDate >= currentMonthStart &&
        transactionDate <= currentMonthEnd
      ) {
        const currentTotal = totalsPerDay.get(day) || currency(0)
        totalsPerDay.set(
          day,
          currentTotal.add(centsToCurrency(transaction.amountInCents))
        )
      }

      if (
        transactionDate >= pastMonthStart &&
        transactionDate <= pastMonthEnd
      ) {
        const pastTotal = pastTotalsPerDay.get(day) || currency(0)
        pastTotalsPerDay.set(
          day,
          pastTotal.add(centsToCurrency(transaction.amountInCents))
        )
      }
    })

    // Calculate cumulative totals for each day
    let currentCumulative = currency(0)
    let pastCumulative = currency(0)
    return times(maxDays).map((i) => {
      const dayOfMonth = i + 1

      // Add cumulative totals for each day
      currentCumulative = currentCumulative.add(
        totalsPerDay.get(dayOfMonth) || 0
      )
      pastCumulative = pastCumulative.add(pastTotalsPerDay.get(dayOfMonth) || 0)

      return {
        current: dayOfMonth <= today.day ? currentCumulative.intValue : null,
        past: pastCumulative.intValue,
        dayOfMonth,
      }
    })
  }
)
